import axios from 'axios';

// Set up axios defaults for authentication
const apiToken = document.head.querySelector('meta[name="api-token"]');
if (apiToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${apiToken.content}`;
    axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
}

const state = {
    loading: false,
    error: null,
    
    catalog: [],
    selection: {
        priceId: null,
        subscriptionId: null,
        update: true,
        quantity: 1,
    },
    preview: null,
    config: null,
    
    // Payment Methods State
    paymentMethods: [],
    setupIntent: null,
    
    // Billing Info State
    billingInfo: {
        name: '',
        email: '',
        phone: '',
        address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postal_code: '',
            country: ''
        },
    },
    
    // Invoices State
    invoices: {
        items: [],
        hasMore: false,
        loading: false
    }
};

// Helper function to format currency
const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

const getters = {
    formattedAmount: (state) => {
        if (!state.preview) return '0.00';
        return state.preview.formatted_amount;
    },

    preview: (state) => {
        return state.preview;
    },
    // Payment Methods Getters
    defaultPaymentMethod: (state) => {
        return state.paymentMethods.find(pm => pm.is_default) || null;
    },
    
    // Billing Info Getters
    formattedBillingAddress: (state) => {
        if (!state.billingInfo.address) return '';
        const { line1, line2, city, state: stateName, postal_code, country } = state.billingInfo.address;
        return [
            line1,
            line2,
            [city, stateName, postal_code].filter(Boolean).join(', '),
            country
        ].filter(Boolean).join('\n');
    },
    
    // Invoices Getters
    formattedInvoices: (state) => {
        return state.invoices.items.map(invoice => ({
            ...invoice,
            formattedDate: new Date(invoice.created * 1000).toLocaleDateString(),
            formattedTotal: formatCurrency(invoice.total / 100, invoice.currency)
        }));
    }
};

const mutations = {
    // General Mutations
    SET_LOADING(state, loading) {
        state.loading = loading;
    },

    SET_ERROR(state, error) {
        state.error = error;
    },

    SET_CONFIG(state, config) {
        state.config = config;
    },

    SET_CATALOG(state, catalog) {
        state.catalog = catalog;
    },


    SET_SELECTION(state, selection) {
        state.selection = { ...state.selection, ...selection };
    },

    SET_PREVIEW(state, preview) {
        state.preview = preview;
    },

    UPDATE_SELECTION(state, selection) {
        state.selection = { ...state.selection, ...selection };
    },
    
    // Payment Methods Mutations
    SET_PAYMENT_METHODS(state, paymentMethods) {
        state.paymentMethods = Array.isArray(paymentMethods) ? paymentMethods : [];
    },
    
    ADD_PAYMENT_METHOD(state, paymentMethod) {
        state.paymentMethods.push(paymentMethod);
    },
    
    UPDATE_PAYMENT_METHOD(state, updatedMethod) {
        const index = state.paymentMethods.findIndex(pm => pm.id === updatedMethod.id);
        if (index !== -1) {
            state.paymentMethods.splice(index, 1, updatedMethod);
        }
    },
    
    REMOVE_PAYMENT_METHOD(state, paymentMethodId) {
        if (!Array.isArray(state.paymentMethods)) {
            state.paymentMethods = [];
        }
        state.paymentMethods = state.paymentMethods.filter(pm => pm && pm.id !== paymentMethodId);
    },
    
    SET_SETUP_INTENT(state, setupIntent) {
        state.setupIntent = setupIntent;
    },
    
    // Billing Info Mutations
    SET_BILLING_INFO(state, billingInfo) {
        state.billingInfo = {
            ...state.billingInfo,
            ...billingInfo,
            address: {
                ...state.billingInfo.address,
                ...(billingInfo.address || {})
            }
        };
    },
    
   
    // Invoices Mutations
    SET_INVOICES(state, { items, hasMore }) {
        state.invoices.items = items;
        state.invoices.hasMore = hasMore;
    },
    
    ADD_INVOICES(state, { items, hasMore }) {
        state.invoices.items = [...state.invoices.items, ...items];
        state.invoices.hasMore = hasMore;
    },
    
    SET_INVOICES_LOADING(state, loading) {
        state.invoices.loading = loading;
    }
};

const actions = {
    async init({ commit, dispatch }, { type }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            await Promise.all([
                dispatch('fetchConfig'),
                dispatch('fetchCatalog'),
                dispatch('fetchPaymentMethods'),
                dispatch('fetchBillingInfo'),
                dispatch('fetchInvoices')
            ]);

            
        } catch (error) {
            commit('SET_ERROR', error.message || 'Failed to initialize billing portal');
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async fetchConfig({ commit }) {
        commit('SET_LOADING', true);
        try {
            const response = await axios.get('/api/billing/config', {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });
            commit('SET_CONFIG', response.data);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to fetch billing configuration';
            commit('SET_ERROR', errorMessage);
            console.error('Billing config error:', errorMessage, error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async fetchCatalog({ commit }) {
        const response = await axios.get('/api/billing/catalog', {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });
        commit('SET_CATALOG', response.data.items);
        console.log('fetchCatalog:', response.data.items);
        return response.data;
    },


    setSelection({ commit }, selection) {
        commit('SET_SELECTION', selection);
    },

    updateAddonQuantity({ commit }, { priceId, quantity }) {
        commit('UPDATE_ADDON_QUANTITY', { priceId, quantity });
    },

    async preview({ commit, state }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        commit('SET_PREVIEW', null); // Clear previous preview

        try {
            const payload = {
                price_id: state.selection.priceId,
                quantity: state.selection.quantity || 1,
                subscription_id: state.selection.subscriptionId || null
            };
            const response = await axios.post('/api/billing/preview', payload, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                console.log('Preview API Response:', response.data);
                commit('SET_PREVIEW', response.data);
                return response.data;
            }

            throw new Error('Invalid preview response');

        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to generate preview';
            commit('SET_ERROR', errorMessage);
            console.error('Preview error:', errorMessage, error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async commit({ commit, state, dispatch }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            let payload = {
                price_id: state.selection.priceId,
                quantity: state.selection.quantity || 1,
                update: state.selection.update || false,
                subscription_id: state.selection.subscriptionId || null
            };

            const response = await axios.post('/api/billing/commit', payload);
            if (response.data) {
               // Handle checkout session URL response
                if (response.data.checkout_session_url) {
                    // Redirect to Stripe Checkout
                    window.location.href = response.data.checkout_session_url;
                    return { redirecting: true };
                }    
                // Handle successful subscription update
                commit('SET_PREVIEW', null);    
                window.location.reload();
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message || 'Failed to commit changes';
            commit('SET_ERROR', errorMessage);
            throw new Error(errorMessage);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    commitChanges: ({ dispatch }) => {
        return dispatch('commit');
    },

    // Payment Methods Actions
    async fetchPaymentMethods({ commit }) {
        try {
            const response = await axios.get('/api/billing/payment-methods');
            const paymentMethods = Array.isArray(response.data) ? response.data : response.data?.data || [];
            commit('SET_PAYMENT_METHODS', paymentMethods);
            return paymentMethods;
        } catch (error) {
            console.error('Failed to fetch payment methods:', error);
            commit('SET_PAYMENT_METHODS', []);
            throw error;
        }
    },

    async createSetupIntent({ commit }) {
        try {
            const response = await axios.post('/api/billing/payment-methods/setup-intent');
            commit('SET_SETUP_INTENT', response.data);
            return response.data;
        } catch (error) {
            console.error('Failed to create setup intent:', error);
            throw error;
        }
    },

    async setDefaultPaymentMethod({ commit, dispatch }, paymentMethodId) {
        try {
            await axios.post('/api/billing/payment-methods/default', {
                payment_method_id: paymentMethodId
            });
            
            // Refresh payment methods to update the default status
            await dispatch('fetchPaymentMethods');
            return true;
        } catch (error) {
            console.error('Failed to set default payment method:', error);
            throw error;
        }
    },

    async deletePaymentMethod({ commit, dispatch, state }, paymentMethodId) {
        try {
            await axios.delete(`/api/billing/payment-methods/${paymentMethodId}`);
            
            // Always refresh the payment methods list to ensure consistency
            await dispatch('fetchPaymentMethods');
            
            return true;
        } catch (error) {
            console.error('Failed to delete payment method:', error);
            // Refresh payment methods to ensure state is in sync
            await dispatch('fetchPaymentMethods').catch(e => console.error('Failed to refresh payment methods:', e));
            throw error;
        }
    },

    // Billing Info Actions
    async fetchBillingInfo({ commit }) {
        try {
            const response = await axios.get('/api/billing/billing-info');
            commit('SET_BILLING_INFO', response.data);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch billing info:', error);
            throw error;
        }
    },

    async updateBillingInfo({ commit, dispatch }, billingInfo) {
        try {
            const response = await axios.put('/api/billing/billing-info', billingInfo);
            
            // Update the billing info in the state
            commit('SET_BILLING_INFO', response.data);
            
            return response.data;
        } catch (error) {
            console.error('Failed to update billing info:', error);
            throw error;
        }
    },

    // Invoices Actions
    async fetchInvoices({ commit, state }, { limit = 10, startingAfter = null } = {}) {
        try {
            commit('SET_INVOICES_LOADING', true);
            
            const params = { limit };
            if (startingAfter) {
                params.starting_after = startingAfter;
            }
            
            const response = await axios.get('/api/billing/invoices', { params });
            
            if (startingAfter) {
                commit('ADD_INVOICES', {
                    items: response.data.data,
                    hasMore: response.data.has_more
                });
            } else {
                commit('SET_INVOICES', {
                    items: response.data.data,
                    hasMore: response.data.has_more
                });
            }
            
            return response.data;
        } catch (error) {
            console.error('Failed to fetch invoices:', error);
            throw error;
        } finally {
            commit('SET_INVOICES_LOADING', false);
        }
    },
    
    async cancelSubscription({ dispatch }, subscriptionId) {
        try {
            await axios.post(`/api/billing/subscriptions/${subscriptionId}/cancel`);
            await dispatch('fetchCatalog');
            return true;
        } catch (error) {
            console.error('Failed to cancel subscription:', error);
            throw error;
        }
    },

    async downloadInvoicePdf(_, invoiceId) {
        try {
            const response = await axios.get(`/api/billing/invoices/${invoiceId}/pdf`, {
                responseType: 'blob'
            });
            
            // Create a URL for the blob
            const url = window.URL.createObjectURL(new Blob([response.data]));
            
            // Create a temporary link element to trigger the download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice-${invoiceId}.pdf`);
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            return true;
        } catch (error) {
            console.error('Failed to download invoice PDF:', error);
            throw error;
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
